import { useState } from "react";
import { useCaseList } from "@/hooks/useCases";
import type { SampleType } from "@/types";
import { sampleTypeLabels } from "@/utils/labels";

interface CreateSampleModalProps {
  caseId: number;
  caseName: string;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    sample_id: string;
    name: string;
    sample_type: SampleType;
    case_id: number;
  }) => void;
  isLoading: boolean;
}

const SAMPLE_TYPES: { value: SampleType; label: string }[] = [
  { value: "gcms", label: sampleTypeLabels.gcms },
  { value: "ftir", label: sampleTypeLabels.ftir },
  { value: "mixed", label: sampleTypeLabels.mixed },
];

export default function CreateSampleModal({
  caseId,
  caseName,
  isOpen,
  onClose,
  onSubmit,
  isLoading,
}: CreateSampleModalProps) {
  const [sampleIdValue, setSampleIdValue] = useState("");
  const [name, setName] = useState("");
  const [sampleType, setSampleType] = useState<SampleType>("gcms");
  const [selectedCaseId, setSelectedCaseId] = useState(caseId);
  const [caseSearch, setCaseSearch] = useState("");

  // Only fetch cases when caseId is not pre-set
  const needsCaseSelection = caseId === 0;
  const { data: cases } = useCaseList();

  const filteredCases = needsCaseSelection
    ? cases?.filter(
        (c) =>
          caseSearch === "" ||
          c.title.toLowerCase().includes(caseSearch.toLowerCase()) ||
          c.case_number.toLowerCase().includes(caseSearch.toLowerCase()),
      )
    : undefined;

  const selectedCase = needsCaseSelection
    ? cases?.find((c) => c.id === selectedCaseId)
    : null;

  const handleSubmit = () => {
    const effectiveCaseId = needsCaseSelection ? selectedCaseId : caseId;
    if (!sampleIdValue.trim() || !name.trim()) {
      alert("시료 ID와 이름은 필수 입력 항목입니다.");
      return;
    }
    if (!effectiveCaseId) {
      alert("사건을 선택해주세요.");
      return;
    }
    onSubmit({
      sample_id: sampleIdValue.trim(),
      name: name.trim(),
      sample_type: sampleType,
      case_id: effectiveCaseId,
    });
  };

  const handleClose = () => {
    setSampleIdValue("");
    setName("");
    setSampleType("gcms");
    setSelectedCaseId(caseId);
    setCaseSearch("");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={handleClose} />
      <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          시료 등록
        </h3>
        {!needsCaseSelection && (
          <p className="text-sm text-gray-500 mb-4">
            사건:{" "}
            <span className="font-medium text-gray-700">{caseName}</span> (ID:{" "}
            {caseId})
          </p>
        )}

        <div className="space-y-4 mb-6">
          {/* 사건 선택 (caseId가 0일 때만 표시) */}
          {needsCaseSelection && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                사건 선택 *
              </label>
              {selectedCase ? (
                <div className="flex items-center space-x-2">
                  <div className="flex-1 px-3 py-2 border border-blue-300 bg-blue-50 rounded-md text-sm">
                    <span className="font-medium">{selectedCase.case_number}</span>
                    {" — "}
                    {selectedCase.title}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedCaseId(0);
                      setCaseSearch("");
                    }}
                    className="px-2 py-2 text-gray-500 hover:text-gray-700"
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <input
                    type="text"
                    value={caseSearch}
                    onChange={(e) => setCaseSearch(e.target.value)}
                    placeholder="사건번호 또는 제목으로 검색..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {caseSearch && filteredCases && filteredCases.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                      {filteredCases.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => {
                            setSelectedCaseId(c.id);
                            setCaseSearch("");
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm border-b border-gray-100 last:border-b-0"
                        >
                          <span className="font-medium text-gray-900">{c.case_number}</span>
                          {" — "}
                          <span className="text-gray-600">{c.title}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {caseSearch && filteredCases && filteredCases.length === 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg p-3 text-sm text-gray-500">
                      일치하는 사건이 없습니다.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              시료 ID *
            </label>
            <input
              type="text"
              value={sampleIdValue}
              onChange={(e) => setSampleIdValue(e.target.value)}
              placeholder="예: SAMPLE-2025-001"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              이름 *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="시료 이름"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              시료 유형
            </label>
            <select
              value={sampleType}
              onChange={(e) => setSampleType(e.target.value as SampleType)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {SAMPLE_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            disabled={isLoading}
          >
            취소
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
            disabled={isLoading}
          >
            {isLoading ? "등록 중..." : "시료 등록"}
          </button>
        </div>
      </div>
    </div>
  );
}
